import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { useAxios } from '@/hooks/useAxios';
import { useCounts } from '@/hooks/useCounts';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useGetAccountAlerts } from '@/hooks/useGetAccountAlerts';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useStableValue } from '@/hooks/useStableValue';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { useOsintContext } from '@/state/OsintContext';
import {
  ITSMTicket,
  NodeDetails,
  QueryFilter,
  ResourceType,
  Risk,
  RiskPriority,
  RiskSeverity,
  RiskStatus,
  RiskTemplate,
  UniqueQueryKeys,
  Vulnerability,
} from '@/types';
import { useMergeStatus, useMutation } from '@/utils/api';
import { buildAttackSurfaceFilter } from '@/utils/attackSurface.util';
import { updateEntityInQueryExecutorCache } from '@/utils/queryExecutorCache';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { isPraetorianOrigin } from '@/utils/source.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Invalidates all risk-related metric queries so dashboard widgets refetch
 * the latest data. Covers:
 * - All statistic-based widgets (HealthGrade, VulnerabilityAnalysis, etc.)
 * - Risk-related generic search widgets (RecentlyDiscoveredVulnerabilities)
 *
 * Called from:
 * - useCreateRisk onSuccess (after a new risk is created)
 * - useUpdateRisk onSuccess (after user changes a risk status)
 * - useRefreshVisited onSuccess (after risk last-seen is refreshed)
 * - HealthGrade.tsx useEffect (when external risk count changes detected via polling)
 *
 * TanStack Query deduplicates in-flight requests, so rapid successive calls
 * are harmless — only one network request per query is made.
 */
export function invalidateRiskMetrics() {
  // Invalidate all statistic-based dashboard widgets
  queryClient.invalidateQueries({
    queryKey: getQueryKey.getMy('statistic'),
  });

  // Invalidate risk-related generic search queries (RecentlyDiscoveredVulnerabilities)
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      return (
        key[0] === UniqueQueryKeys.GENERIC_MY_SEARCH &&
        typeof key[1] === 'string' &&
        key[1].includes('"labels":["Risk"]')
      );
    },
    refetchType: 'active',
  });
}

let ticketSyncTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Invalidates risk queries after a short delay to allow the async
 * ticket-status-change Lambda to finish updating embedded Jira ticket state.
 * Debounced so rapid successive mutations coalesce into a single invalidation.
 */
function invalidateRisksAfterTicketSync() {
  if (ticketSyncTimer) {
    clearTimeout(ticketSyncTimer);
  }
  ticketSyncTimer = setTimeout(() => {
    queryClient.invalidateQueries({
      predicate: query => {
        const queryKey = query.queryKey as string[];
        return queryKey[0] === 'MY' && queryKey[1] === 'risk';
      },
      refetchType: 'active',
    });
    ticketSyncTimer = null;
  }, 5000);
}

let metricsRetryTimers: ReturnType<typeof setTimeout>[] = [];

/**
 * Re-invalidates risk metrics with staggered retries to catch async backend
 * recalculations (health grade, risk stats). The backend Lambda that
 * recalculates the health grade can take 20-30s (cold start + Neo4j query),
 * so retries extend to 45s to ensure the UI picks up the new score.
 * Debounced: rapid successive mutations cancel pending timers.
 */
export function invalidateRiskMetricsWithRetry() {
  metricsRetryTimers.forEach(clearTimeout);
  metricsRetryTimers = [2000, 5000, 12000, 25000, 45000].map(delay =>
    setTimeout(() => {
      invalidateRiskMetrics();
    }, delay)
  );
}

export const useCreateRisk = () => {
  const axios = useAxios();
  const { invalidate: invalidateAccountAlerts } = useGetAccountAlerts({
    enabled: false,
  });
  const { invalidate: invalidateGenericSearch } = useGenericSearch(
    { query: '', label: ResourceType.Risk },
    {
      enabled: false,
    }
  );
  const { invalidate: invalidateRiskCounts } = useCounts(
    { resource: 'risk' },
    { enabled: false }
  );

  return useMutation<Risk, Error, RiskTemplate>({
    defaultErrorMessage: `Failed to create risk`,
    mutationFn: async riskTemplate => {
      const promise = axios
        .put('/risk', { ...riskTemplate, tags: null })
        .then(res => {
          if (!res.data.risks) {
            throw new Error('Failed to create risk');
          }

          return res;
        });

      toast.promise(promise, {
        loading: 'Creating risk...',
        success: 'Risk created',
        error: 'Failed to create risk',
      });
      const { data } = await promise;
      const newRisk = data.risks[0];

      invalidateAccountAlerts();
      invalidateRiskCounts();
      invalidateGenericSearch();
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('risk'),
      });

      invalidateRiskMetrics();

      return newRisk;
    },
  });
};

interface RiskUpdate extends RiskTemplate {
  showSnackbar?: boolean;
}

/**
 * Walk TanStack Query infinite data pages and update a risk by key.
 * Handles both page shapes used by useMy/useInfiniteQuery:
 *   - Tree shape (tree: true):  { data: [{ node: { result: { risks: Risk[] } } }], offset }
 *   - Flat shape (tree: false): { data: Risk[], offset }
 */
function updateRiskInInfiniteData(
  data: unknown,
  riskKey: string,
  patch: Partial<Risk>
): unknown {
  const infiniteData = data as {
    pages?: Array<{ data?: unknown[]; [k: string]: unknown }>;
    pageParams?: unknown[];
  };
  if (!infiniteData?.pages) return data;

  return {
    ...infiniteData,
    pages: infiniteData.pages.map(page => {
      if (!page?.data || !Array.isArray(page.data)) return page;
      return {
        ...page,
        data: page.data.map((item: unknown) => {
          // Tree node shape: { node: { result: { risks: [Risk, ...] } } }
          const treeNode = item as {
            node?: {
              result?: { risks?: Risk[]; [k: string]: unknown };
              [k: string]: unknown;
            };
            [k: string]: unknown;
          };
          if (treeNode?.node?.result?.risks) {
            const hasMatch = treeNode.node.result.risks.some(
              r => r.key === riskKey
            );
            if (hasMatch) {
              return {
                ...treeNode,
                node: {
                  ...treeNode.node,
                  result: {
                    ...treeNode.node.result,
                    risks: treeNode.node.result.risks.map(r =>
                      r.key === riskKey ? { ...r, ...patch } : r
                    ),
                  },
                },
              };
            }
            return item;
          }
          // Flat Risk shape (non-tree queries)
          const risk = item as { key?: string };
          if (risk?.key === riskKey) {
            return { ...risk, ...patch };
          }
          return item;
        }),
      };
    }),
  };
}

export const useUpdateRisk = (props?: {
  onSuccessMessage?: (message: string) => void;
}) => {
  const axios = useAxios();
  const { isOsint } = useOsintContext();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<
    Risk,
    Error,
    RiskUpdate,
    {
      previousQueries: [readonly unknown[], unknown][];
      previousExecutorQueries: [readonly unknown[], unknown][];
    }
  >({
    defaultErrorMessage: `Failed to update risk`,
    mutationFn: async riskUpdate => {
      const { showSnackbar = true, ...riskTemplate } = riskUpdate;

      const isDeleted =
        getRiskStatusLabel(riskTemplate.status).status === RiskStatus.Deleted;

      const riskUrl = isOsint ? '/risk?osint=true' : '/risk';

      const promise = isDeleted
        ? axios.delete(riskUrl, {
            data: {
              key: riskTemplate.key,
              comment: riskTemplate.comment,
              status: riskTemplate.status,
            },
          })
        : axios.put(riskUrl, { ...riskTemplate, tags: null });

      if (showSnackbar) {
        if (isDeleted) {
          toast.promise(promise, {
            loading: 'Closing risks...',
            success: 'Risks Closed',
            error: 'Failed to close risks',
          });
        } else {
          toast.promise(promise, {
            loading: 'Updating risk...',
            success: 'Risk updated',
            error: 'Failed to update risk',
          });
        }
      }

      const { data } = await promise;
      const updatedRisk = data.risks[0] as Risk;

      return updatedRisk;
    },
    onMutate: async variables => {
      // Cancel in-flight risk queries so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
      });

      // Snapshot current cache for rollback
      const previousQueries = queryClient.getQueriesData<unknown>({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
      });

      // Optimistically update the risk in all risk query caches
      queryClient.setQueriesData<unknown>(
        {
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'risk';
          },
        },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          return updateRiskInInfiniteData(old, variables.key, {
            status: variables.status,
          });
        }
      );

      // Also snapshot query-executor cache for rollback
      const previousExecutorQueries = queryClient.getQueriesData<unknown>({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'query-executor';
        },
      });

      // Also update query-executor cache (for query builder views)
      if (variables.key) {
        updateEntityInQueryExecutorCache(queryClient, 'risks', {
          key: variables.key,
          status: variables.status,
        } as { key: string; [k: string]: unknown });
      }

      return { previousQueries, previousExecutorQueries };
    },
    onSuccess: async (data, variables, _context) => {
      invalidateAlerts();

      // Use predicate-based invalidation to catch ALL risk queries
      // This ensures both table and drawer views refetch
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
        refetchType: 'active',
      });

      // Keep specific invalidations for counts and generic search
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getCounts('risk'),
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKey.genericSearch(
          JSON.stringify(variables.key ?? ''),
          undefined,
          undefined
        ),
      });

      const riskName = data?.title ?? data?.name ?? variables.name;
      const riskStatus = data?.status ?? variables.status;

      const { status: updatedStatus, severityLabel } =
        getRiskStatusLabel(riskStatus);

      // When status changes to "Opened", invalidate generic search to refetch ticket data
      // This ensures Jira tickets appear immediately without waiting for polling interval
      // (Matches pattern in VulnerabilityDrawer.tsx handleCreateJiraTicket)
      if (updatedStatus === RiskStatus.Opened) {
        queryClient.invalidateQueries({
          queryKey: getQueryKey.genericSearch(''),
          refetchType: 'active',
        });
      }

      const message =
        updatedStatus === RiskStatus.Remediated
          ? `Great work! ${riskName} has been Remediated`
          : updatedStatus === RiskStatus.Deleted
            ? `${riskName} has been Closed`
            : updatedStatus === RiskStatus.Accepted
              ? `${riskName} has been Accepted`
              : updatedStatus === RiskStatus.Opened
                ? `${riskName} has been moved to Severity ${severityLabel}.`
                : `${riskName} has been Triaged`;

      invalidateRiskMetrics();
      invalidateRiskMetricsWithRetry();

      // Only re-invalidate for ticket sync when status transition affects ticket state
      // (Opened creates/reopens tickets, Remediated/Deleted closes them)
      if (
        updatedStatus === RiskStatus.Opened ||
        updatedStatus === RiskStatus.Remediated ||
        updatedStatus === RiskStatus.Deleted
      ) {
        invalidateRisksAfterTicketSync();
      }

      props?.onSuccessMessage?.(message);
    },
    onError: (error, variables, context) => {
      console.error(`[useUpdateRisk] onError for key: ${variables.key}`, error);
      if (variables.showSnackbar === false) {
        toast.error('Failed to update risk. Please try again.');
      }
      // Rollback optimistic update
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousExecutorQueries) {
        context.previousExecutorQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};

export const useRefreshVisited = () => {
  const axios = useAxios();
  const { isOsint } = useOsintContext();

  return useMutation<Risk, Error, { risk: Risk; comment: string }>({
    defaultErrorMessage: 'Failed to update last seen',
    mutationFn: async ({ risk, comment }) => {
      const visitedUrl = isOsint ? '/risk/visited?osint=true' : '/risk/visited';
      const promise = axios.put(visitedUrl, {
        key: risk.key,
        comment,
      });

      toast.promise(promise, {
        loading: 'Updating last seen...',
        success: 'Last seen updated',
        error: 'Failed to update last seen',
      });

      const { data } = await promise;
      return data.risks[0] as Risk;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
        refetchType: 'active',
      });

      invalidateRiskMetrics();
    },
  });
};

interface ReportRiskProps {
  name: string;
}

export function useReportRisk() {
  const axios = useAxios();

  return useMutation<unknown, Error, ReportRiskProps>({
    defaultErrorMessage: `Failed to generate risk report`,
    mutationFn: async props => {
      const res = await axios.get('/report/risk', {
        params: {
          name: props.name,
        },
      });

      return res;
    },
    onSuccess: (_, props) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: `definitions/${props.name}` }),
      });
    },
  });
}

interface SendThreatNotificationProps {
  vulnerability: Vulnerability;
}

export function useSendThreatNotification() {
  const axios = useAxios();

  return useMutation<Vulnerability, Error, SendThreatNotificationProps>({
    defaultErrorMessage: `Failed to send threat notification`,
    mutationFn: async props => {
      const promise = axios.post('/vulnerability/notify', props.vulnerability);

      toast.promise(promise, {
        loading: 'Sending threat notification...',
        success: 'Threat notification sent to all customers',
        error: 'Failed to send threat notification',
      });

      const { data } = await promise;
      return data.vulnerabilities[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'risk';
        },
        refetchType: 'active',
      });
    },
  });
}

export interface Filter {
  search?: string;
  advancedFilters?: {
    attackSurface?: string[];
    status?: string[];
    severity?: string[];
    kev?: string[];
    cvss?: { min: string; max: string };
    epss?: { min: string; max: string };
    origin?: string[];
    tags?: string[];
    sso?: string[];
    dateRange?: {
      field: 'created' | 'visited';
      startDate: string;
      endDate: string;
    };
    ticket?: string[];
    lastSeen?: string[];
  };
  assetKey?: string;
  huntKey?: string;
  sort?: {
    orderBy: keyof RiskWithVulnerability | 'status';
    descending: boolean;
  };
}

const defaultFilters: Filter = {
  advancedFilters: {
    status: ['O'],
  },
  sort: {
    orderBy: 'priority',
    descending: false,
  },
};

export interface RiskWithVulnerability
  extends Risk,
    Partial<Pick<Vulnerability, 'id'>> {}

/** Column IDs that require the INSTANCE_OF → Vulnerability relationship */
const VULNERABILITY_COLUMN_IDS = ['cvss', 'epss', 'kev'];

export function useGetRisks({
  enabled,
  parentFilters,
  activeColumns,
  initialFilters: initialFiltersOverride,
}: {
  enabled?: boolean;
  parentFilters?: Filter;
  /** Currently visible column IDs — used to decide whether to fetch vulnerability data */
  activeColumns?: string[];
  initialFilters?: Filter;
}) {
  // When parentFilters exist (drawer context), use ephemeral state so drawer
  // filters never leak to the main table. Every drawer open starts fresh.
  const isDrawerContext = !!parentFilters;

  const { isPraetorianUser, whoami } = useAuth();
  const customerType = whoami?.tenant?.customer_type;
  const showDetectedForAccountType =
    customerType === 'SAAS' || customerType === 'FREEMIUM';

  const [persistedFilters, setPersistedFilters] = useStorage<Filter>(
    {
      queryKey: 'vulnerabilityFilters',
      key: StorageKey.FILTERS_RISKS,
      ephemeralKeys: ['search'],
    },
    defaultFilters
  );

  // Sanitize stale status filters: non-Praetorian users should never see the
  // Deleted ('D') status. If a customer previously had 'D' in their persisted
  // filters (e.g., from a Praetorian impersonation session) strip it out so it
  // does not silently ghost their results.
  const sanitizedPersistedFilters: Filter = useMemo(() => {
    if (isPraetorianUser) return persistedFilters;
    const status = persistedFilters.advancedFilters?.status;
    if (!status || !status.includes(RiskStatus.Deleted))
      return persistedFilters;
    return {
      ...persistedFilters,
      advancedFilters: {
        ...persistedFilters.advancedFilters,
        status: status.filter(s => s !== RiskStatus.Deleted),
      },
    };
  }, [isPraetorianUser, persistedFilters]);

  const [ephemeralFilters, setEphemeralFilters] = useState<Filter>(
    initialFiltersOverride ?? defaultFilters
  );

  const localFilters = isDrawerContext
    ? ephemeralFilters
    : sanitizedPersistedFilters;
  const setFilters = isDrawerContext
    ? setEphemeralFilters
    : setPersistedFilters;

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const filters: Filter = parentFilters
    ? { ...localFilters, ...parentFilters }
    : localFilters;
  // Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  // The debounced value was lagging behind the actual search value, preventing React Query from refetching
  const [debouncedSearch] = useDebounce(
    filters.search,
    filters.search === '' ? 0 : 300
  );

  const stableAdvancedFilters = useStableValue(filters.advancedFilters);
  const stableSort = useStableValue(filters.sort);
  const stableFilters = useStableValue(filters);

  const {
    nodeFilters,
    relationshipFilters,
    globalFilters,
    isFiltersApplied,
    vulnerabilityFilters,
    isOrderedByVulnerability,
  } = useMemo(() => {
    const nodeFilters: QueryFilter[] = [];
    const relationshipFilters: NodeDetails['relationships'] = [];
    const vulnerabilityFilters: QueryFilter[] = [];
    const assetFilters: QueryFilter[] = [];
    const globalFilters: QueryFilter[] = [];

    const advancedFilters = stableAdvancedFilters;

    // Search is handled via node.search (fulltext index) instead of CONTAINS filters

    if (advancedFilters?.status && advancedFilters.status.length > 0) {
      nodeFilters.push({
        field: 'status',
        operator: 'STARTS WITH',
        value: advancedFilters.status,
      });
    }

    if (advancedFilters?.severity && advancedFilters.severity.length > 0) {
      nodeFilters.push({
        field: 'priority',
        operator: '=',
        value: advancedFilters.severity.map(
          severity => RiskPriority[severity as RiskSeverity]
        ),
      });
    }

    if (advancedFilters?.kev && advancedFilters.kev.length > 0) {
      vulnerabilityFilters.push({
        field: 'kev',
        operator: '=',
        value: advancedFilters.kev.map(kev => kev === 'true'),
      });
    }

    if (advancedFilters?.cvss) {
      const min = Number(advancedFilters.cvss.min);
      const max = Number(advancedFilters.cvss.max);
      const isSame = min === max;

      vulnerabilityFilters.push({
        field: 'cvss',
        operator: '>=',
        value: min,
      });
      vulnerabilityFilters.push({
        field: 'cvss',
        operator: isSame ? '<' : '<=',
        value: isSame ? min + 0.001 : max,
      });
    }

    if (advancedFilters?.epss) {
      const min = Number(advancedFilters.epss.min);
      const max = Number(advancedFilters.epss.max);

      vulnerabilityFilters.push({
        field: 'epss',
        operator: '>=',
        value: min,
      });
      vulnerabilityFilters.push({
        field: 'epss',
        operator: '<=',
        value: max,
      });
    }

    if (advancedFilters?.origin && advancedFilters.origin.length > 0) {
      // Check if manual (@praetorian.com) is included in the selected origins
      const hasManual = advancedFilters.origin.includes('@praetorian.com');
      const nonManualOrigins = advancedFilters.origin.filter(
        origin => origin !== '@praetorian.com'
      );
      const hasNonManual = nonManualOrigins.length > 0;

      const hasManualFilter: QueryFilter = {
        field: 'source',
        operator: 'ENDS WITH',
        value: '@praetorian.com',
      };

      const hasNonManualOriginsFilter: QueryFilter = {
        field: 'origins',
        operator: 'IN',
        value: nonManualOrigins,
        reverseOperands: true,
      };

      if (hasManual && hasNonManual) {
        nodeFilters.push({
          field: '',
          operator: 'OR',
          value: [hasNonManualOriginsFilter, hasManualFilter],
        });
      } else if (hasManual) {
        nodeFilters.push(hasManualFilter);
      } else if (hasNonManual) {
        nodeFilters.push(hasNonManualOriginsFilter);
      }
    }

    if (filters.assetKey) {
      assetFilters.push({
        field: 'key',
        operator: '=',
        value: filters.assetKey,
      });
    }

    if (advancedFilters?.attackSurface?.length) {
      const surfaceFilter = buildAttackSurfaceFilter(
        advancedFilters.attackSurface
      );
      // After migration, Risk nodes have denormalized isExternal/isInternal/isCloud/isRepository
      // flags — filter directly on the Risk node for NodeIndexSeek performance
      if (surfaceFilter) nodeFilters.push(surfaceFilter);
    }

    if (advancedFilters?.tags && advancedFilters.tags.length > 0) {
      nodeFilters.push({
        field: 'tags',
        operator: 'IN',
        value: advancedFilters.tags,
        not: false,
        reverseOperands: true,
      });
    }

    if (advancedFilters?.dateRange) {
      const { field, startDate, endDate } = advancedFilters.dateRange;

      // Convert dates to ISO timestamp format for backend comparison
      nodeFilters.push({
        field,
        operator: '>=',
        value: `${startDate}T00:00:00Z`,
      });
      nodeFilters.push({
        field,
        operator: '<=',
        value: `${endDate}T23:59:59Z`,
      });
    }

    if (advancedFilters?.lastSeen && advancedFilters.lastSeen.length > 0) {
      const duration = advancedFilters.lastSeen[0];
      const now = new Date();
      let hoursAgo = 24; // default

      if (duration.endsWith('h')) {
        hoursAgo = parseInt(duration, 10);
      } else if (duration.endsWith('d')) {
        hoursAgo = parseInt(duration, 10) * 24;
      }

      const cutoff = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
      nodeFilters.push({
        field: 'visited',
        operator: '>=',
        value: cutoff.toISOString(),
      });
    }

    if (advancedFilters?.sso && advancedFilters.sso.length > 0) {
      const filterSsoIdentified = advancedFilters.sso.includes('true');
      const filterNotSsoIdentified = advancedFilters.sso.includes('false');
      const isAllSelected = filterSsoIdentified && filterNotSsoIdentified;

      if (isAllSelected) {
        // Both selected: match both web-login-sso and web-login-generic
        globalFilters.push({
          field: 'name',
          operator: 'STARTS WITH',
          value: 'web-login',
          alias: 'risk',
        });
      } else if (filterSsoIdentified) {
        // Only SSO identified selected
        globalFilters.push({
          field: 'name',
          operator: '=',
          value: 'web-login-sso',
          alias: 'risk',
        });
      } else {
        // Only non-SSO selected
        globalFilters.push({
          field: 'name',
          operator: '=',
          value: 'web-login-generic',
          alias: 'risk',
        });
      }
    }

    if (assetFilters.length > 0) {
      relationshipFilters.push({
        label: ['HAS_VULNERABILITY', 'HAS_PORT', 'HAS_WEBPAGE'],
        length: 3,
        source: {
          labels: ['Asset'],
          filters: assetFilters,
        },
      });
    }

    if (filters.huntKey) {
      // Risk -[REPORTED_BY]-> Hunt {key: huntKey}
      relationshipFilters.push({
        label: ['REPORTED_BY'],
        target: {
          labels: ['Hunt'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: filters.huntKey,
            },
          ],
        },
      });
    }

    if (advancedFilters?.ticket && advancedFilters.ticket.length > 0) {
      const hasTicket = advancedFilters.ticket.includes('has-ticket');
      const noTicket = advancedFilters.ticket.includes('no-ticket');
      // When only one option is selected, add server-side filter
      if (hasTicket && !noTicket) {
        nodeFilters.push({
          field: 'tickets',
          operator: 'IS NOT NULL',
          value: '',
        });
      } else if (noTicket && !hasTicket) {
        nodeFilters.push({
          field: 'tickets',
          operator: 'IS NULL',
          value: '',
        });
      }
      // When both selected, no filter needed (show all)
    }

    return {
      nodeFilters,
      relationshipFilters: relationshipFilters.map(filter => ({
        ...filter,
        select_one: true,
      })),
      vulnerabilityFilters,
      globalFilters,
      isFiltersApplied:
        nodeFilters.length > 0 ||
        relationshipFilters.length > 0 ||
        vulnerabilityFilters.length > 0 ||
        globalFilters.length > 0 ||
        (!!debouncedSearch && debouncedSearch.length >= 2),
      isOrderedByVulnerability:
        stableSort?.orderBy &&
        ['cvss', 'epss', 'kev'].includes(stableSort.orderBy),
    };
  }, [
    stableAdvancedFilters,
    filters.assetKey,
    filters.huntKey,
    stableSort,
    debouncedSearch,
  ]);

  // Include INSTANCE_OF when vulnerability columns are visible, sorted, or filtered
  const needsVulnerabilityData =
    vulnerabilityFilters.length > 0 ||
    isOrderedByVulnerability ||
    (activeColumns?.some(col => VULNERABILITY_COLUMN_IDS.includes(col)) ??
      false);

  // Optimization #2: Skip allQuery when parentFilters exist (drawer context)
  // In drawer with parentFilters, we know risks exist for the asset
  // This query is redundant and only needed for the main view's "empty state" detection
  const shouldFetchAll = !parentFilters;

  // BUGFIX: Add existence query to check if asset has ANY vulnerabilities
  // This is needed for drawer context to distinguish between:
  // - Asset with vulnerabilities that are filtered out (show toolbar, clear filters)
  // - Asset with zero vulnerabilities (show NoData, hide toolbar)
  // Uses limit: 1 for efficiency - we only need to know if ANY exist
  // IMPORTANT: Must use tree: true for graph queries with relationships
  const existenceQuery = useMy(
    {
      resource: 'risk',
      tree: true,
      query: {
        node: {
          labels: ['Risk'],
          relationships: [
            {
              label: ['HAS_VULNERABILITY', 'HAS_PORT', 'HAS_WEBPAGE'],
              length: 3,
              source: {
                labels: ['Asset'],
                filters: [
                  {
                    field: 'key',
                    operator: '=',
                    value: parentFilters?.assetKey || '',
                  },
                ],
              },
              select_one: true,
            },
          ],
        },
        limit: 1,
      },
    },
    { enabled: Boolean(parentFilters?.assetKey) }
  );

  const allQuery = useMy(
    {
      resource: 'risk',
      query: {
        node: {
          labels: ['Risk'],
        },
        limit: 1,
      },
    },
    { enabled: enabled && shouldFetchAll }
  );

  const query = useMy(
    {
      resource: 'risk',
      tree: true,
      query: {
        node: {
          alias: 'risk',
          labels: ['Risk'],
          // Fulltext search — covers key, name, dns, title via riskSearchIndex
          ...(debouncedSearch && debouncedSearch.length >= 2
            ? { search: debouncedSearch }
            : {}),
          relationships: [
            // Include INSTANCE_OF when vulnerability columns are visible,
            // sorted by a vulnerability field, or vulnerability filters are active
            ...(needsVulnerabilityData
              ? [
                  {
                    label: 'INSTANCE_OF' as const,
                    target: {
                      alias: 'vuln',
                      labels: ['Vulnerability' as const],
                      sorted: isOrderedByVulnerability,
                      filters:
                        vulnerabilityFilters.length > 0
                          ? vulnerabilityFilters
                          : undefined,
                    },
                    optional: vulnerabilityFilters.length === 0,
                    select_one: false,
                  },
                ]
              : []),
            ...relationshipFilters,
          ],
          filters: nodeFilters.length > 0 ? nodeFilters : undefined,
        },
        ...(filters.sort && filters.sort.orderBy === 'priority'
          ? {
              orderByFields: [
                { field: 'priority', descending: !!filters.sort.descending },
                { field: 'visited', descending: !filters.sort.descending },
              ],
            }
          : filters.sort && filters.sort.orderBy === 'status'
            ? {
                orderByFields: [
                  {
                    field: 'status',
                    descending: !!filters.sort.descending,
                  },
                ],
              }
            : filters.sort && filters.sort.orderBy !== 'status'
              ? {
                  orderBy: filters.sort.orderBy,
                  descending: filters.sort.descending,
                }
              : {}),
        filters: globalFilters.length > 0 ? globalFilters : undefined,
      },
    },
    {
      enabled,
      // Keep previous data visible during search to prevent blanking on every keystroke
      // Search is excluded from filterKey, so table doesn't remount
      // Filter/sort changes still remount via filterKey for scroll reset
      placeholderData: keepPreviousData,
      // Always fetch fresh data when parentFilters change (e.g., opening asset drawer)
      // Matches the pattern in useVulnerabilityDrawerData.ts for consistent behavior
      staleTime: 0,
    }
  );

  // Reset selection when filters change (adjusting state based on props pattern)
  // Using state to track previous value avoids both useEffect and ref issues
  // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevFilters, setPrevFilters] = useState(stableFilters);
  if (prevFilters !== stableFilters) {
    setPrevFilters(stableFilters);
    if (selectedRows.length > 0) {
      setSelectedRows([]);
    }
  }

  let data = query.data.map((riskTree): RiskWithVulnerability => {
    const risk = riskTree?.node?.result?.risks?.[0] as Risk;

    // Find the INSTANCE_OF relationship (may not exist when vuln filters inactive)
    const vulnRelationship = riskTree?.node?.relationships?.find(
      (rel: { label: string }) => rel.label === 'INSTANCE_OF'
    );
    const vulnerability = vulnRelationship?.target?.result
      ?.vulnerabilities?.[0] as Vulnerability | undefined;

    const tickets = risk?.tickets;
    const ticket: ITSMTicket | null =
      tickets && tickets.length > 0
        ? {
            provider: tickets[0].provider,
            ticketId: tickets[0].id,
            link: tickets[0].link ?? '',
            status: tickets[0].status ?? '',
          }
        : null;

    return {
      ...risk,
      // Only overwrite with vulnerability data when INSTANCE_OF was fetched,
      // otherwise preserve the risk's own fields
      ...(vulnerability
        ? {
            title: vulnerability.title || risk?.title,
            cvss: vulnerability.cvss,
            epss: vulnerability.epss,
            exploit: vulnerability.exploit,
            kev: vulnerability.kev,
            id: vulnerability.id,
          }
        : {}),
      ticket,
    };
  });

  // BUGFIX: Deduplicate risks by key to prevent duplicate rows in table
  // This handles cases where graph queries with OPTIONAL relationships return
  // the same risk multiple times (once per matched relationship).
  // Same issue as assets (commit 6fb4c3171) - backend ScanTree returns duplicate risks
  // Related: CHARIOT-1567
  const seenKeys = new Set<string>();
  data = data.filter(risk => {
    if (seenKeys.has(risk.key)) {
      return false; // Skip duplicate
    }
    seenKeys.add(risk.key);
    return true; // Keep first occurrence
  });

  // Hide Praetorian-origin detected risks from customer users.
  // Third-party integration origins (Wiz, Nessus, etc.) remain visible.
  // SAAS and FREEMIUM accounts see all detected risks.
  if (!isPraetorianUser && !showDetectedForAccountType) {
    data = data.filter(risk => {
      if (!risk.status?.startsWith('T')) return true;
      return !isPraetorianOrigin(risk.source, risk.origins);
    });
  }

  // Client-side sorting for status column to achieve logical ordering
  // Backend sorts alphabetically, but we want importance-based ordering
  if (filters.sort?.orderBy === 'status') {
    // Define logical sort order: Opened -> Triaged -> Remediated -> Accepted -> Deleted
    const statusOrder: Record<string, number> = {
      O: 1, // Opened - most important
      T: 2, // Triaged - being reviewed
      R: 3, // Remediated - closed
      I: 4, // Accepted
      D: 5, // Deleted/Rejected
    };

    data = [...data].sort((a, b) => {
      const aStatusCode = a.status?.[0] || 'Z';
      const bStatusCode = b.status?.[0] || 'Z';
      const aOrder = statusOrder[aStatusCode] || 999;
      const bOrder = statusOrder[bStatusCode] || 999;

      const diff = aOrder - bOrder;
      // Reverse if descending
      return filters.sort?.descending ? -diff : diff;
    });
  }

  const status = useMergeStatus(
    shouldFetchAll ? allQuery.status : 'success', // Don't merge disabled query status
    query.status
  );

  // Build activeQuery unconditionally so bulk selection ("Select all matching") and export work regardless of whether filters are applied
  const activeQuery = {
    node: {
      alias: 'risk',
      labels: ['Risk'],
      ...(debouncedSearch && debouncedSearch.length >= 2
        ? { search: debouncedSearch }
        : {}),
      relationships: [
        ...(needsVulnerabilityData
          ? [
              {
                label: 'INSTANCE_OF' as const,
                target: {
                  alias: 'vuln',
                  labels: ['Vulnerability' as const],
                  sorted: isOrderedByVulnerability,
                  filters:
                    vulnerabilityFilters.length > 0
                      ? vulnerabilityFilters
                      : undefined,
                },
                optional: vulnerabilityFilters.length === 0,
                select_one: false,
              },
            ]
          : []),
        ...relationshipFilters,
      ],
      filters: nodeFilters.length > 0 ? nodeFilters : undefined,
    },
    ...(filters.sort && filters.sort.orderBy === 'priority'
      ? {
          orderByFields: [
            { field: 'priority', descending: !!filters.sort.descending },
            { field: 'visited', descending: !filters.sort.descending },
          ],
        }
      : filters.sort && filters.sort.orderBy === 'status'
        ? {
            orderByFields: [
              {
                field: 'status',
                descending: !!filters.sort.descending,
              },
            ],
          }
        : filters.sort && filters.sort.orderBy !== 'status'
          ? {
              orderBy: filters.sort.orderBy,
              descending: filters.sort.descending,
            }
          : {}),
    filters: globalFilters.length > 0 ? globalFilters : undefined,
  };

  return {
    data,
    error: allQuery.error || query.error || existenceQuery.error,
    status,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetching: query.isFetching,
    hasNextPage: query.hasNextPage,
    refetch: () => {
      query.refetch();
      allQuery.refetch();
      existenceQuery.refetch();
    },
    invalidate: () => {
      query.invalidate();
      allQuery.invalidate();
      existenceQuery.invalidate();
    },
    // BUGFIX: Fix isAllEmpty for drawer context using existence query
    // In main view: use allQuery to check if account has ANY risks
    // In drawer view: use existenceQuery to check if asset has ANY vulnerabilities
    // This ensures toolbar remains visible when filters return no results but asset has vulnerabilities
    isAllEmpty: shouldFetchAll
      ? status !== 'pending' && allQuery.data.length === 0 // Main view
      : existenceQuery.status !== 'pending' && existenceQuery.data.length === 0, // Drawer view (use existence query)
    isFilteredEmpty: status !== 'pending' && data.length === 0,
    isFiltersApplied,
    filters,
    setFilters,
    selectedRows,
    setSelectedRows,
    activeQuery,
  };
}
